import { Measure } from '@cbsi-cbscom/measure';
import { IAutoplayInfo } from 'library/ts/interfaces/IAutoplayInfo';
import { getLanguageLabelByCode, Language } from 'library/ts/model/language/Language';
import { IPARA } from 'library/ts/namespaces/PlayerServices';
import { IPlayerAdapterDeviceCapabilities } from 'library/ts/player/adapter/interfaces/capabilities/IPlayerAdapterDeviceCapabilities';
import { IPlayerAdapterConstructor } from 'library/ts/player/adapter/IPlayerAdapterConstructor';
import { Cookies } from 'library/ts/utilities/Cookies';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Metrics } from 'library/ts/utilities/measure/Metrics';
import { ConnectionMonitor } from 'library/ts/utilities/network/ConnectionMonitor';
import { ISmartTag } from 'tag/ts/smart_tag/interfaces/ISmartTag';
import { ISmartTagRuntimeVO } from 'tag/ts/smart_tag/interfaces/ISmartTagRuntimeVO';
import { PublicEnums } from 'tag/ts/smart_tag/model/enums/PublicEnums';
import { SmartTagOperations } from 'tag/ts/smart_tag/operations/SmartTagOperations';
import { TTagCreateData } from 'tag/ts/smart_tag/types/api/TTagCreateData';
import { TTagRemoveData } from 'tag/ts/smart_tag/types/api/TTagRemoveData';
import { TTagResetData } from 'tag/ts/smart_tag/types/api/TTagResetData';
import { getSmartTagRuntimeVO } from 'tag/ts/smart_tag/utilities/Runtime';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { ICallbacksVO } from 'tag/ts/tag/interfaces/callbacks/ICallbacksVO';
import { ITagEventVO } from 'tag/ts/tag/interfaces/events/ITagEventVO';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { AviaAdapter } from 'tag/ts/tag/player/adapters/avia/AviaAdapter';
import { PlayerAdapterType } from 'tag/ts/tag/player/enums/PlayerAdapterType';
import { Tag } from 'tag/ts/tag/Tag';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description SmartTag namespace
 */
export class SmartTag implements ISmartTag {
	readonly #enums: PublicEnums;
	#connectionMonitor: ConnectionMonitor;
	#DETECT_AUTOPLAY: boolean = true;
	#autoplay: IAutoplayInfo;
	// stores device capabilities data for STV
	deviceCapabilities: IPlayerAdapterDeviceCapabilities;
	#runtimeVO: ISmartTagRuntimeVO;
	#logger: ILogger;
	#operations: SmartTagOperations;

	/**
	 * @description Tag registry by uuid
	 */
	#registry: { [ key: string ]: ITag } = {};
	/**
	 * @description Tag list in array format
	 */
	#list: ITag[] = [];

	/**
	 * @description Constructor
	 * @param {IPARA} _para - Unused parameter, kept for API compatibility
	 */
	constructor( _para: IPARA = window?.PARA ) {
		this.#enums = new PublicEnums();
		this.#logger = LogService.create( { prefixes: [ 'SmartTag:' ] } );

		// One-time cleanup of the corrupt `<prefix>-undefined` localStorage key left behind by
		// pre-PLBCK-8210 builds when a torn-down Tag's settings write raced a stray post-teardown
		// caller. Store.remove is a no-op if the key is already gone, so this is safe on every page load.
		this.utilities?.Store?.remove?.( `${window?.PlayerServicesProjectInfo?.app_storage_prefix?.smart_tag?.value}-undefined` );

		this.#operations = new SmartTagOperations( {
			logger: this.#logger,
			utilities: this.utilities,
			capabilities: this.capabilities,
			detectAutoplay: this.#DETECT_AUTOPLAY,
			smartTagInstance: this,
			// Setters for private properties
			setConnectionMonitor: ( monitor: ConnectionMonitor | undefined ) => {
				this.#connectionMonitor = monitor;
			},
			getConnectionMonitor: () => this.#connectionMonitor,
			setAutoplay: ( info: IAutoplayInfo ) => {
				this.#autoplay = info;
			},
			// Getters for private properties
			getRegistry: () => this.#registry,
			getList: () => this.#list,
			// Controlled setters
			setList: ( list: ITag[] ) => {
				this.#list = list;
			},
			setRegistry: ( registry: { [ key: string ]: ITag } ) => {
				this.#registry = registry;
			},
			// Helper methods for collection management
			removeFromList: ( uuid: string ) => {
				const index = this.utilities?.Array?.indexOf?.( 'uuid', uuid, this.#list );
				if ( index !== null && index !== undefined ) {
					this.#list.splice( index, 1 );
				}
			},
			removeFromRegistry: ( uuid: string ) => {
				delete this.#registry[ uuid ];
			}
		} );

		// Ensure utilities is available before using it
		if ( this.utilities?.Delay?.delay ) {
			this.utilities.Delay.delay().then(
				() => {
					this.#runtimeVO = getSmartTagRuntimeVO(); // get runtime data

					this.#logger.log( 'this.#runtimeVO?.readyCallback?.detectAutoplay', this.#runtimeVO?.readyCallback?.detectAutoplay );

					if ( !this.#runtimeVO?.readyCallback?.detectAutoplay ) {
						this.#DETECT_AUTOPLAY = false;
					}

					this.#start(); // start application
				}
			).catch( ( error ) => {
				this.#logger.error( 'Error during initialization delay:', error );
			} );
		} else {
			this.#logger.error( 'Utilities.Delay not available' );
		}
	}

	/**
	 * @description Returns logger instance
	 */
	get logger() {
		return this.#logger;
	}

	/**
	 * @description Returns autoplay detection setting
	 */
	get DETECT_AUTOPLAY() {
		return this.#DETECT_AUTOPLAY;
	}

	/**
	 * @description Returns connection monitor instance
	 */
	get connectionMonitor() {
		return this.#connectionMonitor;
	}

	/**
	 * @description Sets connection monitor instance
	 */
	set connectionMonitor( value: ConnectionMonitor ) {
		this.#connectionMonitor = value;
	}

	/**
	 * @description Sets autoplay info
	 */
	set autoplay( value: IAutoplayInfo ) {
		this.#autoplay = value;
	}

	/**
	 * @description Returns SmartTag name
	 */
	get name() {
		return 'SmartTag';
	}

	/**
	 * @description Returns SmartTag version and build time
	 */
	get info() {
		return {
			version: window?.PlayerServicesProjectInfo?.release_version || 'unknown',
			date: window?.PlayerServicesProjectInfo?.app_compile_time?.tag?.token || 'unknown'
		};
	}

	/**
	 * @description Returns session start time
	 */
	get startTime() {
		return window?.PARA?.PlayerServices?.StartTime;
	}

	/**
	 * @description Returns instance of Utilities
	 */
	get utilities() {
		return window?.PARA?.PlayerServices?.Utilities;
	}

	/**
	 * @description Returns instance of Cookies singleton
	 */
	get cookies() {
		return Cookies.getInstance();
	}

	/**
	 * @description Returns instance of Capabilities
	 */
	get capabilities() {
		return window?.PARA?.PlayerServices?.Capabilities;
	}

	/**
	 * @description Returns online status from ConnectionManager
	 */
	get online() {
		return this.#connectionMonitor !== undefined
			? this.#connectionMonitor.onLine
			: undefined;
	}

	/**
	 * @description Returns autoplay detection results
	 */
	get autoplay() {
		return this.#autoplay;
	}

	/**
	 * @description Returns instance of RuntimeVO
	 */
	get runtimeVO() {
		return this.#runtimeVO;
	}

	/**
	 * @description Tag registry. List of Tag applications where each object key is Tag application uuid
	 */
	get registry(): { [ key: string ]: ITag } {
		return this.#registry;
	}

	set registry( value: { [ key: string ]: ITag } ) {
		this.#registry = value;
	}

	/**
	 * @description Tag list array for easy access via index
	 */
	get list(): ITag[] {
		return this.#list;
	}

	set list( value: ITag[] ) {
		this.#list = value;
	}

	/**
	 * @description Publicly exposed enums / types
	 */
	get enums() {
		return this.#enums;
	}

	/**
	 * @description Returns reference to Language module and its method for getting label string per ISO language code
	 */
	get Language() {
		return {
			list: Language,
			getLanguageLabelByCode: getLanguageLabelByCode
		};
	}

	/**
	 * @description Dictionary of available non instantiated player adapter classes
	 */
	get playerAdapters(): Record<PlayerAdapterType, IPlayerAdapterConstructor> {
		return {
			[ PlayerAdapterType.AVIA ]: AviaAdapter
		};
	}

	/**
	 * @description Reference to Smart Tag's set of Measure metrics
	 */
	get performanceMetrics() {
		return Measure?.metrics;
	}

	/**
	 *  @description Initializes Tag class SmartTag bare-bones inside of page container
	 * @param {IParamsVO} paramsVO
	 * @param {ICallbacksVO} callbacksVO
	 * @returns {Promise<TTagCreateData>}
	 */
	create = ( paramsVO: IParamsVO, callbacksVO: ICallbacksVO = {} ): Promise<TTagCreateData> => {
		this.#logger.log( 'create:', paramsVO );
		Measure.end( Metrics.importToCreateTime );
		Measure.start( Metrics.createToLoadTime );
		return new Promise(
			( onReadyResolve, onReadyReject ) => {
				// TODO: check if Tag instance with the same PID, Content ID or page container already exists
				const tag: ITag = new Tag(
					{
						paramsVO: paramsVO,
						callbacksVO: callbacksVO
					}
				); // create instance of tag
				this.registry[ tag.uuid ] = tag; // store Tag object in smart tag registry
				// if tag with the same uuid does not exist in list add it
				if ( this.utilities?.Array?.hasObjWithPropValue &&
					this.utilities.Array.hasObjWithPropValue( this.list, 'uuid', tag.uuid ) === false ) {
					this.list.push( tag );
				}

				// Ensure the shared connection monitor exists. It is created at #start() and torn down
				// when the registry empties (last removeTag / reset); re-create it here when a tag is
				// added back to a previously-empty registry so the new tag still receives online/offline.
				if ( !this.#connectionMonitor ) {
					this.#operations.initializeConnectionMonitor();
				}

				// Re-arm the avia unhandledrejection suppressor — it is unregistered when the registry
				// empties, so a tag created into an empty registry would otherwise run with no suppression.
				this.#operations.ensureAviaErrorHandler();

				// Listen for controller ready event (fires after ALL initialization is complete)
				// Using IEventContextVO with timesToDispatch: 1 for auto-cleanup after first call
				tag.eventDispatcher.addEventListener(
					{ type: TagEvent.ON_CONTROLLER_READY, timesToDispatch: 1 },
					( _event: ITagEventVO ) => {
						onReadyResolve( { msg: 'TagEvent.ON_CONTROLLER_READY', tag: tag } );
					}
				);

				// Listen for fatal error to reject promise
				// Using IEventContextVO with timesToDispatch: 1 for auto-cleanup after first call
				tag.eventDispatcher.addEventListener(
					{ type: TagEvent.ON_FATAL_ERROR, timesToDispatch: 1 },
					( event: ITagEventVO ) => {
						onReadyReject( { msg: 'TagEvent.ON_FATAL_ERROR', tag: tag, error: event?.data } );
					}
				);
			}
		);
	};

	/**
	 * @description Remove Tag instance
	 * @param {ITag} tag
	 * @return {Promise<TTagRemoveData>}
	 */
	remove = ( tag: ITag ): Promise<TTagRemoveData> => {
		return this.#operations.removeTag( tag );
	};

	/**
	 * @description Reset SmartTag to its initial state resetting + removing all Tags from registry
	 * @return {Promise<TTagResetData>}
	 */
	reset = (): Promise<TTagResetData> => {
		return this.#operations.resetTags();
	};

	/**
	 * @description Start the application
	 */
	#start = () => {
		this.#logger.log( 'Start' );
		this.#operations.initializeConnectionMonitor();
		this.#operations.handleAutoplayDetection();
	};
}